import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Link2, FileText, Check, Minus, ExternalLink, Trash2, Download, Upload, Loader2, Plus, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Section, telecharger, dateFr } from './shared';

const erreur = (e, msg) => toast.error(e.response?.data?.detail || msg);

const BoutonSupprimer = ({ onConfirm, testId }) => {
  const [arme, setArme] = useState(false);
  return arme ? (
    <span className="flex items-center gap-1">
      <Button size="sm" variant="destructive" className="h-7 px-2 text-xs" onClick={onConfirm} data-testid={`${testId}-confirm`}>Supprimer</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setArme(false)}>Annuler</Button>
    </span>
  ) : (
    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => setArme(true)} data-testid={testId}><Trash2 className="h-4 w-4" /></Button>
  );
};

export const PreuvesAppli = ({ preuves, onNavigate }) => (
  <Section titre="Preuves de l'application" icone={Database} compteur={preuves.filter((p) => p.ok).length} testId="qualiopi-preuves-appli">
    {preuves.length === 0 ? <p className="text-sm text-slate-400">Aucune donnée de l'application liée à cet indicateur.</p> : (
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {preuves.map((p, i) => (
          <li key={p.libelle} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`qualiopi-preuve-appli-${i}`}>
            {p.ok ? <Check className="h-4 w-4 shrink-0 text-emerald-600" /> : <Minus className="h-4 w-4 shrink-0 text-slate-300" />}
            <span className={`flex-1 ${p.ok ? 'text-slate-700' : 'text-slate-400'}`}>{p.libelle}</span>
            <span className="font-mono text-xs font-semibold text-slate-600">{p.valeur}</span>
            {p.pdf && (
              <button type="button" onClick={() => telecharger(p.pdf, `${p.libelle.replace(/\s*\(tableau PDF\)/, '').replace(/[^\p{L}\d]+/gu, '_').replace(/_+$/, '')}.pdf`).catch(() => toast.error('PDF indisponible'))}
                className="text-slate-400 hover:text-[#0F172A]" title="Télécharger le tableau PDF" data-testid={`qualiopi-preuve-pdf-${i}`}><Download className="h-4 w-4" /></button>
            )}
            {p.lien && <Link to={p.lien} onClick={onNavigate} className="text-slate-400 hover:text-[#0F172A]" title="Ouvrir la page"><ArrowUpRight className="h-4 w-4" /></Link>}
          </li>
        ))}
      </ul>
    )}
  </Section>
);

export const LiensSection = ({ num, liens, onUpdate }) => {
  const [titre, setTitre] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const ajouter = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onUpdate((await axios.post(`/qualiopi/indicateurs/${num}/liens`, { titre, url })).data);
      setTitre(''); setUrl('');
      toast.success('Lien ajouté');
    } catch (err) { erreur(err, "Impossible d'ajouter le lien"); } finally { setBusy(false); }
  };
  const supprimer = async (id) => {
    try { onUpdate((await axios.delete(`/qualiopi/indicateurs/${num}/liens/${id}`)).data); } catch (err) { erreur(err, 'Suppression impossible'); }
  };
  return (
    <Section titre="Liens (site fisl.fr et autres)" icone={Link2} compteur={liens.length} testId="qualiopi-liens">
      {liens.map((l) => (
        <div key={l.lien_id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`qualiopi-lien-${l.lien_id}`}>
          <a href={l.url} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-2 text-[#0F172A] hover:underline">
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" /> <span className="truncate">{l.titre}</span>
          </a>
          {l.source === 'fisl.fr' && <span className="rounded bg-[#8DC63F]/15 px-1.5 text-[10px] font-semibold text-[#5a8a1f]">fisl.fr</span>}
          <BoutonSupprimer onConfirm={() => supprimer(l.lien_id)} testId={`qualiopi-lien-supprimer-${l.lien_id}`} />
        </div>
      ))}
      <form onSubmit={ajouter} className="flex flex-col gap-2 sm:flex-row">
        <Input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Titre du lien" className="sm:w-48" data-testid="qualiopi-lien-titre" />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" required className="flex-1" data-testid="qualiopi-lien-url" />
        <Button type="submit" variant="outline" disabled={busy || !url} data-testid="qualiopi-lien-ajouter"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
      </form>
    </Section>
  );
};

export const DocumentsSection = ({ num, documents, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const envoyer = async (e) => {
    const fichiers = [...(e.target.files || [])];
    e.target.value = '';
    if (!fichiers.length) return;
    setBusy(true);
    for (const f of fichiers) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        onUpdate((await axios.post(`/qualiopi/indicateurs/${num}/documents`, fd, { timeout: 120000 })).data);
        toast.success(`« ${f.name} » ajouté`);
      } catch (err) { erreur(err, `Échec de l'envoi de « ${f.name} »`); }
    }
    setBusy(false);
  };
  const supprimer = async (id) => {
    try { onUpdate((await axios.delete(`/qualiopi/indicateurs/${num}/documents/${id}`)).data); } catch (err) { erreur(err, 'Suppression impossible'); }
  };
  return (
    <Section titre="Documents déposés" icone={FileText} compteur={documents.length} testId="qualiopi-documents">
      {documents.map((d) => (
        <div key={d.doc_id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`qualiopi-document-${d.doc_id}`}>
          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[#0F172A]">{d.nom}</span>
            <span className="block text-xs text-slate-400">{dateFr(d.uploaded_at)}{d.uploaded_by ? ` · ${d.uploaded_by}` : ''}</span>
          </span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => telecharger(`/qualiopi/documents/${d.doc_id}`, d.nom, 60000).catch(() => toast.error('Téléchargement impossible'))} data-testid={`qualiopi-document-telecharger-${d.doc_id}`}><Download className="h-4 w-4" /></Button>
          <BoutonSupprimer onConfirm={() => supprimer(d.doc_id)} testId={`qualiopi-document-supprimer-${d.doc_id}`} />
        </div>
      ))}
      <input ref={input} type="file" multiple className="hidden" onChange={envoyer} data-testid="qualiopi-document-input" />
      <Button variant="outline" className="w-full border-dashed" disabled={busy} onClick={() => input.current?.click()} data-testid="qualiopi-document-ajouter">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Envoi en cours…</> : <><Upload className="h-4 w-4 mr-2" /> Déposer un document (10 Mo max)</>}
      </Button>
    </Section>
  );
};
